import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Upload, Plus, Trash2, FileText, Presentation, ChevronLeft, ChevronRight, User, LogOut, FolderOpen, Settings } from 'lucide-react';
import { createPresentation, createSession, getPresentation, updatePresentation } from '../services/api';
import { toast } from '../hooks/use-toast';
import { usePrompt } from '../hooks/usePrompt';
import pdfjs from '../config/pdfConfig';
import PDFSlideViewer from '../components/PDFSlideViewer';
import AIDepthGenerator from '../components/AIDepthGenerator';
import BackgroundSelector from '../components/BackgroundSelector';
import RichTextEditor from '../components/RichTextEditor';
import PresentationsDashboard from './PresentationsDashboard';

const UploadSetup = ({ onLogout }) => {
  const navigate = useNavigate();
  const { presentationId } = useParams(); // Get presentation ID from URL if editing
  const [currentStep, setCurrentStep] = useState(1);
  const [presentationTitle, setPresentationTitle] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [slides, setSlides] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPresentationId, setEditingPresentationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  const [lastSavedTime, setLastSavedTime] = useState(null);

  // Get user info from localStorage
  const userId = localStorage.getItem('user_id');
  const username = localStorage.getItem('username') || 'User';
  
  // Prompt user before navigating away with unsaved changes
  usePrompt(
    hasUnsavedChanges,
    'You have unsaved changes. Are you sure you want to leave?'
  );
  
  // Track changes to mark as unsaved
  useEffect(() => {
    // Track changes in step 2 for both create and edit modes
    if (currentStep === 2 && (presentationTitle || slides.length > 0)) {
      if (isEditMode) {
        setHasUnsavedChanges(true);
        setAutoSaveStatus('unsaved');
      } else {
        // For new presentations, mark as unsaved but don't trigger auto-save
        setAutoSaveStatus('unsaved');
      }
    }
  }, [presentationTitle, slides, isEditMode, currentStep]);
  
  // Auto-save functionality (only for edit mode)
  useEffect(() => {
    if (!isEditMode || !editingPresentationId || !hasUnsavedChanges) {
      return;
    }

    // Auto-save after 30 seconds of inactivity
    const autoSaveTimer = setTimeout(async () => {
      await performAutoSave();
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [presentationTitle, slides, isEditMode, editingPresentationId, hasUnsavedChanges]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Auto-save function
  const performAutoSave = async () => {
    if (!isEditMode || !editingPresentationId || slides.length === 0) {
      return;
    }

    try {
      setAutoSaveStatus('saving');
      
      const presentationData = {
        title: presentationTitle,
        user_id: userId,
        pdf_data: pdfBase64,
        slides: slides.map((slide, index) => ({
          title: slide.title,
          content: slide.content,
          depth_notes: slide.depthNotes,
          additional_resources: slide.additionalResources || '',
          pdf_page_number: slide.pdfPageNumber || 0,
          order: index,
          background: slide.background || { type: 'color', value: '#ffffff' }
        }))
      };

      await updatePresentation(editingPresentationId, presentationData);
      
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');
      setLastSavedTime(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('unsaved');
    }
  };

  // Manual save function
  const handleManualSave = async () => {
    await performAutoSave();
    alert('Presentation saved successfully!');
  };
  
  // Load presentation data if editing
  useEffect(() => {
    const loadPresentation = async () => {
      if (presentationId) {
        try {
          setLoading(true);
          const presentation = await getPresentation(presentationId);
          
          // Set presentation data
          setPresentationTitle(presentation.title);
          setPdfBase64(presentation.pdf_data);
          setIsEditMode(true);
          setEditingPresentationId(presentationId);
          
          // Convert slides to the format used by the editor
          const loadedSlides = presentation.slides.map(slide => ({
            id: slide.id,
            title: slide.title,
            content: slide.content,
            depthNotes: slide.depth_notes,
            additionalResources: slide.additional_resources,
            pdfPageNumber: slide.pdf_page_number,
            background: slide.background || { type: 'color', value: '#ffffff' }
          }));
          
          setSlides(loadedSlides);
          
          // If there's PDF data, calculate page count
          if (presentation.pdf_data) {
            const pdfBlob = await fetch(`data:application/pdf;base64,${presentation.pdf_data}`).then(r => r.blob());
            const pdfDoc = await pdfjs.getDocument(URL.createObjectURL(pdfBlob)).promise;
            setPdfPageCount(pdfDoc.numPages);
          }
          
          // Go to step 2 (Edit Slides)
          setCurrentStep(2);
          
          // Reset unsaved changes flag after loading
          setHasUnsavedChanges(false);
          setAutoSaveStatus('saved');
          
        } catch (error) {
          console.error('Error loading presentation:', error);
          alert('Failed to load presentation for editing');
          navigate('/upload-setup');
        } finally {
          setLoading(false);
        }
      } else {
        // Reset to create mode if no presentation ID
        setIsEditMode(false);
        setEditingPresentationId(null);
        setPresentationTitle('');
        setSlides([]);
        setPdfBase64(null);
        setCurrentStep(1);
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
      }
    };
    
    loadPresentation();
  }, [presentationId, navigate]);
  
  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split('@')[0]
      .split(/[._-]/)
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Helper function to strip HTML tags for preview text
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File uploaded:', file.name, file.type, 'Size:', file.size);
      
      // Check file size (5MB limit)
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 5) {
        alert('File size exceeds 5MB limit. Please upload a smaller file.');
        e.target.value = null;
        return;
      }
      
      setUploadedFile(file);
      setPresentationTitle(file.name.replace(/\.[^/.]+$/, ''));
      setPdfLoading(true);
      
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isDocxOrPpt = ['docx', 'ppt', 'pptx'].includes(fileExtension);
      
      try {
        let pdfData;
        
        if (isDocxOrPpt) {
          // Convert DOCX/PPT to PDF via backend
          console.log(`Converting ${fileExtension.toUpperCase()} to PDF...`);
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/convert-file`, {
            method: 'POST',
            body: formData
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Conversion failed');
          }
          
          const result = await response.json();
          pdfData = result.pdf_data;
          console.log(`✓ ${fileExtension.toUpperCase()} converted to PDF successfully`);
          
        } else if (file.type === 'application/pdf') {
          // Convert PDF to base64
          const reader = new FileReader();
          
          pdfData = await new Promise((resolve, reject) => {
            reader.onload = (event) => {
              const base64 = event.target.result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          
          console.log('✓ PDF converted to base64, size:', (pdfData.length / 1024).toFixed(2), 'KB');
        } else {
          throw new Error('Unsupported file type');
        }
        
        setPdfBase64(pdfData);
        
        // Get page count from converted PDF
        try {
          const uint8Array = new Uint8Array(atob(pdfData).split('').map(c => c.charCodeAt(0)));
          
          const loadPdfWithTimeout = Promise.race([
            pdfjs.getDocument({ data: uint8Array }).promise,
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('PDF loading timeout')), 5000)
            )
          ]);
          
          const pdf = await loadPdfWithTimeout;
          setPdfPageCount(pdf.numPages);
          console.log(`✓ PDF loaded: ${pdf.numPages} pages`);
        } catch (error) {
          console.warn('Could not get page count, using default:', error.message);
          setPdfPageCount(10);
        }
        
        setPdfLoading(false);
        
      } catch (error) {
        console.error('✗ Error processing file:', error);
        setPdfLoading(false);
        alert('Error processing file: ' + error.message);
        e.target.value = null;
        setUploadedFile(null);
      }
    }
  };

  const handleAutoGenerate = async () => {
    if (!pdfBase64 || pdfPageCount === 0) {
      alert('Please wait for the PDF to finish processing');
      return;
    }
    
    // Auto-generate slides from PDF pages
    const generatedSlides = Array.from({ length: pdfPageCount }, (_, i) => ({
      id: `slide-${Date.now()}-${i}`,
      title: '',
      content: '',
      depthNotes: '',
      additionalResources: '',
      pdfPageNumber: i + 1,
      background: { type: 'color', value: '#ffffff' }
    }));
    
    setSlides(generatedSlides);
    setCurrentStep(2);
    
    // Scroll to the first slide editor after a small delay
    setTimeout(() => {
      const firstEditor = document.querySelector('[data-slide-editor]');
      if (firstEditor) {
        firstEditor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCreateFromScratch = () => {
    // Ensure presentation title is set
    if (!presentationTitle.trim()) {
      setPresentationTitle('Untitled Presentation');
    }
    
    // Create one default slide with no PDF page
    const defaultSlide = {
      id: `slide-${Date.now()}`,
      title: 'Slide 1',
      content: 'Start editing your content here...',
      depthNotes: '',
      additionalResources: '',
      pdfPageNumber: 0, // No PDF page
      background: { type: 'color', value: '#ffffff' }
    };
    
    setSlides([defaultSlide]);
    setPdfBase64(null); // No PDF data
    setPdfPageCount(0);
    setCurrentStep(2);
    
    // Scroll to the slide editor
    setTimeout(() => {
      const firstEditor = document.querySelector('[data-slide-editor]');
      if (firstEditor) {
        firstEditor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const addSlide = () => {
    const newSlideId = Date.now();
    setSlides([...slides, { 
      id: newSlideId, 
      title: `Slide ${slides.length + 1}`, 
      content: '', 
      depthNotes: '',
      additionalResources: '',
      pdfPageNumber: 0,
      background: { type: 'color', value: '#ffffff' }
    }]);
    
    // Scroll to the newly added slide after a short delay to ensure DOM is updated
    setTimeout(() => {
      const element = document.getElementById(`slide-editor-${newSlideId}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const updateSlide = (id, field, value) => {
    setSlides(slides.map(slide => 
      slide.id === id ? { ...slide, [field]: value } : slide
    ));
  };

  const deleteSlide = (id) => {
    setSlides(slides.filter(slide => slide.id !== id));
  };

  const startPresentation = async () => {
    if (slides.length === 0) {
      alert('Please add at least one slide');
      return;
    }
    
    // Validate user_id exists
    if (!userId) {
      console.error('No user_id found in localStorage!');
      alert('Session expired. Please log in again.');
      navigate('/');
      return;
    }
    
    console.log('Creating/updating presentation for user:', userId);
    
    try {
      // Prepare presentation data
      const presentationData = {
        title: presentationTitle,
        user_id: userId,
        pdf_data: pdfBase64,
        slides: slides.map((slide, index) => ({
          title: slide.title,
          content: slide.content,
          depth_notes: slide.depthNotes,
          additional_resources: slide.additionalResources || '',
          pdf_page_number: slide.pdfPageNumber || 0,
          order: index,
          background: slide.background || { type: 'color', value: '#ffffff' }
        }))
      };
      
      console.log('Presentation data:', {
        title: presentationData.title,
        user_id: presentationData.user_id,
        slides_count: presentationData.slides.length,
        isEditMode
      });
      
      let presentation;
      
      // Update existing presentation or create new one
      if (isEditMode && editingPresentationId) {
        console.log('Updating presentation:', editingPresentationId);
        presentation = await updatePresentation(editingPresentationId, presentationData);
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        setLastSavedTime(new Date());
        console.log('Presentation updated successfully:', presentation.id);
        alert('Presentation updated successfully!');
      } else {
        console.log('Creating new presentation');
        presentation = await createPresentation(presentationData);
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        setLastSavedTime(new Date());
        console.log('Presentation created successfully:', presentation.id);
      }
      
      // Create session
      const session = await createSession(presentation.id);
      
      // Navigate to presenter dashboard
      navigate(`/presenter/${session.id}`);
    } catch (error) {
      console.error('Error saving presentation:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save presentation. Please try again.';
      alert(errorMsg);
    }
  };

  // Show loading state while loading presentation for edit
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading presentation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* User Profile Menu */}
      <div className="absolute top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold flex items-center justify-center hover:shadow-lg transition-all"
          >
            {getInitials(username)}
          </button>
          
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{username}</p>
                <p className="text-xs text-gray-500 truncate">Signed in</p>
              </div>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  navigate('/my-presentations');
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <FolderOpen className="w-4 h-4" />
                My Presentations
              </button>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  navigate('/settings');
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Account Settings
              </button>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  onLogout();
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Header with Step Indicator */}
        <div className="mb-12">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                {isEditMode ? 'Edit Your Presentation' : 'Create Your Presentation'}
              </h1>
              <p className="text-slate-600 text-lg">
                {isEditMode ? 'Update your slides and content' : 'Upload content and setup your dual-layer presentation'}
              </p>
              
              {/* Auto-save status indicator - Show in Step 2 */}
              {currentStep === 2 && (presentationTitle || slides.length > 0) && (
                <div className="mt-2 flex items-center gap-2">
                  {autoSaveStatus === 'saving' && (
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      <span>Saving...</span>
                    </div>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <div 
                      className="relative group flex items-center gap-2 text-sm text-green-600 cursor-pointer"
                      title={lastSavedTime ? `Last saved at ${lastSavedTime.toLocaleString()}` : ''}
                    >
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      <span>Saved</span>
                      
                      {/* Tooltip on hover */}
                      {lastSavedTime && (
                        <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          Last saved at {lastSavedTime.toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                  {autoSaveStatus === 'unsaved' && (
                    <div className="flex items-center gap-2 text-sm text-orange-600">
                      <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                      <span>Unsaved</span>
                      {isEditMode && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={handleManualSave}
                          className="ml-2 h-6 text-xs"
                        >
                          Save Now
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {typeof onLogout === 'function' && (
              <Button onClick={onLogout} variant="outline" size="sm">
                Logout
              </Button>
            )}
          </div>
          
          {/* Step Indicator */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentStep(1)}
              className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-blue-600' : 'text-slate-400'} hover:opacity-80 transition-opacity cursor-pointer`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                1
              </div>
              <span className="font-medium">Upload</span>
            </button>
            <div className={`h-0.5 w-12 ${currentStep >= 2 ? 'bg-blue-600' : 'bg-slate-300'}`}></div>
            <button
              onClick={() => slides.length > 0 && setCurrentStep(2)}
              disabled={slides.length === 0}
              className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-blue-600' : 'text-slate-400'} ${slides.length > 0 ? 'hover:opacity-80 transition-opacity cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                2
              </div>
              <span className="font-medium">Edit Slides</span>
            </button>
            <div className={`h-0.5 w-12 ${currentStep >= 3 ? 'bg-blue-600' : 'bg-slate-300'}`}></div>
            <button
              onClick={() => slides.length > 0 && setCurrentStep(3)}
              disabled={slides.length === 0}
              className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-blue-600' : 'text-slate-400'} ${slides.length > 0 ? 'hover:opacity-80 transition-opacity cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                3
              </div>
              <span className="font-medium">Launch</span>
            </button>
          </div>
        </div>

        {/* Step 1: Upload Section */}
        {currentStep === 1 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <Upload className="w-5 h-5 text-blue-600" />
                Upload Content
              </CardTitle>
              <CardDescription>Upload a PDF, DOCX, PPT, or PPTX file to begin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="file-upload" className="text-slate-700">Document File (Max 5MB)</Label>
                  <div className="mt-2">
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.docx,.doc,.ppt,.pptx"
                      onChange={handleFileUpload}
                      className="cursor-pointer"
                    />
                  </div>
                  {uploadedFile && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <FileText className="w-4 h-4" />
                        <span>{uploadedFile.name}</span>
                        {pdfLoading && (
                          <Badge variant="secondary" className="ml-2 animate-pulse">
                            {uploadedFile.name.match(/\.(docx|ppt|pptx)$/i) ? 'Converting...' : 'Processing...'}
                          </Badge>
                        )}
                        {!pdfLoading && pdfPageCount > 0 && (
                          <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                            {pdfPageCount} pages ✓
                          </Badge>
                        )}
                      </div>
                      {!pdfLoading && pdfPageCount === 0 && uploadedFile.type === 'application/pdf' && (
                        <p className="text-xs text-amber-600">
                          ⚠️ PDF not loaded. Check console for errors.
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="title" className="text-slate-700">Presentation Title</Label>
                  <Input
                    id="title"
                    value={presentationTitle}
                    onChange={(e) => setPresentationTitle(e.target.value)}
                    placeholder="Enter presentation title"
                    className="mt-2"
                  />
                </div>

                <div className="space-y-3">
                  <Button 
                    onClick={handleAutoGenerate}
                    disabled={!uploadedFile || pdfLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <Presentation className="w-4 h-4 mr-2" />
                    {pdfLoading ? 'Processing...' : 'Auto-Generate Slides'}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-300" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">Or</span>
                    </div>
                  </div>

                  <Button 
                    onClick={handleCreateFromScratch}
                    variant="outline"
                    className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create from Scratch
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Slide Preview & Editor */}
        {currentStep === 2 && (
          <>
            {/* Presentation Title Editor */}
            <Card className="border-slate-200 shadow-sm mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label htmlFor="presentation-title-edit" className="text-slate-700 font-semibold">
                      Presentation Name
                    </Label>
                    <Input
                      id="presentation-title-edit"
                      value={presentationTitle}
                      onChange={(e) => setPresentationTitle(e.target.value)}
                      placeholder="Enter presentation name"
                      className="mt-2 text-lg font-medium"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Slide Preview Grid */}
            <Card className="border-slate-200 shadow-sm mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-900">Slide Preview</CardTitle>
                    <CardDescription>Overview of all slides in your presentation</CardDescription>
                  </div>
                  <Button onClick={addSlide} variant="outline" className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add Slide
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {slides.map((slide, index) => (
                    <Card 
                      key={`slide-preview-${slide.id}`}
                      className="border-slate-200 hover:border-blue-300 transition-all cursor-pointer group overflow-hidden"
                      onClick={() => {
                        const element = document.getElementById(`slide-editor-${slide.id}`);
                        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      <div className="aspect-video bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col justify-center border-b border-slate-200 overflow-hidden relative">
                        <div className="text-xs font-semibold text-blue-600 mb-2 absolute top-2 left-2 bg-white/90 rounded px-2 py-1 z-10">
                          Slide {index + 1}
                        </div>
                        {pdfBase64 && slide.pdfPageNumber > 0 ? (
                          <div className="w-full h-full flex items-center justify-center bg-white p-2">
                            <PDFSlideViewer 
                              key={`pdf-preview-${slide.id}-${slide.pdfPageNumber}`}
                              pdfData={pdfBase64}
                              pageNumber={slide.pdfPageNumber}
                              width={280}
                              maintainAspectRatio={false}
                            />
                          </div>
                        ) : (
                          <div className="p-4 pt-10">
                            <h3 className="font-bold text-slate-900 text-sm line-clamp-2 mb-2">
                              {stripHtml(slide.title) || 'Untitled Slide'}
                            </h3>
                            <p className="text-xs text-slate-600 line-clamp-3">
                              {stripHtml(slide.content) || 'No content yet'}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-white flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {slide.depthNotes && (
                            <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">
                              Has depth notes
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSlide(slide.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Slides Editor */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Edit Slides</CardTitle>
                <CardDescription>Click on any field to edit slide content and depth notes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {slides.map((slide, index) => (
                    <Card key={slide.id} id={`slide-editor-${slide.id}`} className="border-slate-200 scroll-mt-6">
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-900">Slide {index + 1}</h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSlide(slide.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Live Slide Preview */}
                        <div className="mb-4">
                          <Label className="text-slate-700">Slide Preview</Label>
                          <div 
                            className="mt-2 border-2 border-slate-200 rounded-lg overflow-hidden p-8 aspect-video flex items-center justify-center"
                            style={{
                              background: slide?.background?.type === 'color' 
                                ? slide.background.value 
                                : slide?.background?.type === 'gradient'
                                ? slide.background.value
                                : slide?.background?.type === 'image'
                                ? `url(${slide.background.value}) center/cover`
                                : '#ffffff'
                            }}
                          >
                            {pdfBase64 && slide.pdfPageNumber > 0 ? (
                              <PDFSlideViewer 
                                pdfData={pdfBase64}
                                pageNumber={slide.pdfPageNumber}
                                width={600}
                                maintainAspectRatio={false}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col justify-center">
                                {slide.title && (
                                  <h2 
                                    className="text-3xl font-bold text-slate-900 mb-4"
                                    dangerouslySetInnerHTML={{ __html: slide.title }}
                                  />
                                )}
                                {slide.content && (
                                  <div 
                                    className="text-slate-700 text-lg leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: slide.content }}
                                  />
                                )}
                                {!slide.title && !slide.content && (
                                  <p className="text-slate-400 text-center italic">
                                    Your slide content will appear here
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <RichTextEditor
                            value={slide.title}
                            onChange={(content) => updateSlide(slide.id, 'title', content)}
                            placeholder="Slide title"
                            label="Title"
                            minHeight="80px"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div>
                            <RichTextEditor
                              value={slide.content}
                              onChange={(content) => updateSlide(slide.id, 'content', content)}
                              placeholder="Main slide content"
                              label="Content"
                              minHeight="250px"
                            />
                          </div>
                          
                          <div>
                            <BackgroundSelector
                              value={slide.background || { type: 'color', value: '#ffffff' }}
                              onChange={(bg) => updateSlide(slide.id, 'background', bg)}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-700">Speaker Notes (Depth Layer)</Label>
                          <Textarea
                            value={slide.depthNotes}
                            onChange={(e) => updateSlide(slide.id, 'depthNotes', e.target.value)}
                            placeholder="Additional in-depth content for viewers"
                            rows={8}
                            className="mt-1 border-blue-200"
                          />
                          <AIDepthGenerator
                            slideTitle={slide.title}
                            slideContent={slide.content}
                            onGenerated={(formattedNotes) => {
                              updateSlide(slide.id, 'depthNotes', formattedNotes);
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-slate-700">Additional Resources</Label>
                          <Textarea
                            value={slide.additionalResources || ''}
                            onChange={(e) => updateSlide(slide.id, 'additionalResources', e.target.value)}
                            placeholder="Add links, text, or image URLs (one per line)"
                            rows={3}
                            className="mt-1 border-green-200"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Add text, links, or image URLs. Each on a new line.
                          </p>
                        </div>

                        {/* Add Slide Button - Bottom Right */}
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={addSlide}
                            variant="outline"
                            size="sm"
                            className="gap-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                          >
                            <Plus className="w-4 h-4" />
                            Add Slide
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-3 mt-8">
                  <Button
                    onClick={() => setCurrentStep(1)}
                    variant="outline"
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(3)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={slides.length === 0}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Step 3: Review & Launch */}
        {currentStep === 3 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900">Ready to Launch</CardTitle>
              <CardDescription>Review your presentation and start when ready</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Presentation Summary */}
                <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-4">Presentation Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Title:</span>
                      <span className="font-medium text-slate-900">{presentationTitle}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total Slides:</span>
                      <span className="font-medium text-slate-900">{slides.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Slides with Depth Notes:</span>
                      <span className="font-medium text-slate-900">
                        {slides.filter(s => s.depthNotes).length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Slide List with PDF Previews */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Slides</h3>
                  <div className="space-y-2">
                    {slides.map((slide, index) => (
                      <div key={slide.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-8 h-8 rounded bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                          {index + 1}
                        </div>
                        
                        {/* PDF Preview Thumbnail */}
                        {pdfBase64 && slide.pdfPageNumber > 0 && (
                          <div className="w-24 h-16 flex-shrink-0 border border-slate-200 rounded overflow-hidden bg-white">
                            <PDFSlideViewer 
                              pdfData={pdfBase64}
                              pageNumber={slide.pdfPageNumber}
                              width={96}
                            />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">{stripHtml(slide.title) || 'Untitled'}</h4>
                          <p className="text-sm text-slate-600 line-clamp-1">
                            {slide.pdfPageNumber > 0 ? `PDF Page ${slide.pdfPageNumber}` : (stripHtml(slide.content) || 'No content')}
                          </p>
                        </div>
                        {slide.depthNotes && (
                          <Badge variant="secondary" className="text-xs">Depth</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-3 mt-8">
                  <Button
                    onClick={() => setCurrentStep(2)}
                    variant="outline"
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back to Edit
                  </Button>
                  <Button
                    onClick={startPresentation}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-lg py-6"
                  >
                    <Presentation className="w-5 h-5 mr-2" />
                    Start Presentation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UploadSetup;
